import { ref, watch, computed, onUnmounted, type Ref } from 'vue';
import type { ShowResultsMessage } from '@ilteducation/quizter-types';
import { addLeaderboardPositions } from '../util/sortResults';

type Result = ShowResultsMessage['payload']['results'][number];

/**
 * Composable for animating score changes in the leaderboard
 * Provides smooth transitions when scores update
 */
export function useScoreAnimation(results: Ref<Result[]>) {
  // Animation state
  const animatedScores = ref<Map<string, number>>(new Map());
  const isAnimating = ref(false);
  let animationFrameId: number | null = null;
  let animationStartTime: number | null = null;
  const ANIMATION_DURATION = 1500; // 1.5 seconds

  // Easing function for smooth animation
  const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  };

  // Create animated results that update during animation
  const animatedResults = computed(() => {
    const currentResults = results.value;

    if (!isAnimating.value) {
      return currentResults;
    }

    return currentResults.map((res) => ({
      ...res,
      totalPoint: animatedScores.value.get(res.player.toString()) ?? res.totalPoint,
    }));
  });

  // Sort results by score and add positions
  const sortedResults = computed(() => {
    return addLeaderboardPositions(animatedResults.value);
  });

  // Animation logic
  const animateScores = (previousScores: Map<string, number>, targetScores: Map<string, number>) => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
    }

    isAnimating.value = true;
    animationStartTime = performance.now();

    const animate = (currentTime: number) => {
      if (animationStartTime === null) {
        animationStartTime = currentTime;
      }

      const elapsed = currentTime - animationStartTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const easedProgress = easeOutCubic(progress);

      // Update all animated scores
      targetScores.forEach((targetScore, playerId) => {
        const startScore = previousScores.get(playerId) ?? 0;
        const currentScore = startScore + (targetScore - startScore) * easedProgress;
        animatedScores.value.set(playerId, Math.round(currentScore));
      });

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        // Animation complete
        isAnimating.value = false;
        animationFrameId = null;
        animationStartTime = null;
      }
    };

    animationFrameId = requestAnimationFrame(animate);
  };

  // Watch for results changes and trigger animation
  watch(
    results,
    (newResults) => {
      // Create maps of current and new scores
      const previousScores = new Map<string, number>();
      const targetScores = new Map<string, number>();

      // Calculate previous scores and initialize animation
      // Previous score = totalPoint - lastQuestionPoint (points from current round)
      newResults.forEach((res) => {
        const previousScore = res.totalPoint - (res.lastQuestionPoint || 0);
        previousScores.set(res.player.toString(), previousScore);
        targetScores.set(res.player.toString(), res.totalPoint);
        animatedScores.value.set(res.player.toString(), previousScore);
      });

      // Start animation
      animateScores(previousScores, targetScores);
    },
    { immediate: true },
  );

  // Cleanup on unmount
  onUnmounted(() => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
    }
  });

  return {
    animatedResults,
    sortedResults,
    isAnimating,
  };
}
